import { useState, useEffect } from 'react';
import api from '../lib/api';
import { SoulAIUsageTab } from './admin/SoulAIUsageTab';
import DietCommunicationTab from './admin/DietCommunicationTab';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { CountrySignalsCard } from './admin/CountrySignalsCard';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { X, Calendar, Phone, Mail, Plus, Trash2, Video, ExternalLink, Clock, Pencil, Save, Loader2, MoreVertical, XCircle, Star, AlertTriangle, BookOpen, GraduationCap, Landmark, CreditCard, FileText, Award, Download, RefreshCw, User as UserIcon, Info, ShieldCheck } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Textarea } from './ui/textarea';
import { formatDate, formatDateTime, formatTime, formatTimeLocal, getTimezoneAbbr, isIST, convertISTToLocal, getInitials, getDurationLabel, getStatusColor } from '../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth-context';
import CoursesTabPanel from './CoursesTabPanel';
import { TierBadge, eliteAvatarRing, isEliteTier } from './TierBadge';
import { ProductAccessControl } from './admin/ProductAccessControl';
import { StoreSubscriptionsCard } from './admin/StoreSubscriptionsCard';
import { MembershipExpiryCard } from './admin/MembershipExpiryCard';

export default function UserDetailModal({ userId, open, onClose, onUpdate }) {
  const { user: currentUser } = useAuth();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('batches');
  const [allBatches, setAllBatches] = useState(null);
  // V59 — the 90 KB batch list is only needed for the "Add to batch" dropdown; fetch it on first open
  const loadAssignableBatches = () => {
    if (allBatches !== null) return;
    setAllBatches([]);
    api.get('/api/batches', { params: { limit: 100 } })
      .then(res => setAllBatches(res.data.batches || []))
      .catch(() => setAllBatches([]));
  };
  const [assignBatchId, setAssignBatchId] = useState('');
  const [sessionView, setSessionView] = useState('upcoming');
  const [sessionMonth, setSessionMonth] = useState('all');   // V78 — month filter for Attended / Past
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', email: '', phone: '' });
  const [releasePhoneOpen, setReleasePhoneOpen] = useState(false);
  const [eddDialogOpen, setEddDialogOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [payoutConfig, setPayoutConfig] = useState({ rate_30min: 0, rate_45min: 0, rate_60min: 0, rate_90min: 0, no_show_rate: 0 });
  const [payoutSaving, setPayoutSaving] = useState(false);
  const [payoutEditing, setPayoutEditing] = useState(false);
  const [availSlots, setAvailSlots] = useState([]);
  const [availSaving, setAvailSaving] = useState(false);
  // Instructor profile & scorecard states
  const [scorecard, setScorecard] = useState(null);
  const [profileEditing, setProfileEditing] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [instructorRatings, setInstructorRatings] = useState(null);
  const [bufferSettings, setBufferSettings] = useState({ instructor_start_buffer: 10, learner_join_buffer: 5 });
  // V14.5 Part 19s — MTD Payouts now reads from the SAME source as the
  // Finance dashboard (/api/finance/instructor-payouts?month=YYYY-MM
  // &instructor_id=<id>) so both screens never disagree again.
  // Founder reproduced: User-Modal said "0 sessions this month" while
  // Finance showed 134 — caused by computing locally from past_sessions
  // (which is capped at 60 + missed `cancelled` UK spelling + had no
  // month filter). Single source of truth = the Finance endpoint.
  const [financePayout, setFinancePayout] = useState(null);
  const [feedbackList, setFeedbackList] = useState(null);
  const [financeMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [profileForm, setProfileForm] = useState({
    bio: '', experience: '', degrees: '',
    bank_name: '', bank_account: '', ifsc: '', upi_id: '', pan_number: '',
    name: '', email: '', phone: '',
  });
  const isSuperadmin = currentUser?.role === 'superadmin';

  useEffect(() => {
    if (open && userId) {
      setLoading(true);
      setTab('batches');
      setEditMode(false);
      setPayoutEditing(false);
      setProfileEditing(false);
      setScorecard(null);
      setFeedbackList(null);
      api.get('/api/settings/buffers').then(res => setBufferSettings(res.data)).catch(() => {});
      setAllBatches(null);
      api.get(`/api/users/${userId}/detail`).then((userRes) => {
        const u = userRes.data;
        setUser(u);
        setEditForm({ name: u.name || '', email: u.email || '', phone: u.phone || '' });
        // Populate instructor profile form
        if (u.role === 'instructor') {
          setProfileForm({
            bio: u.bio || '', experience: u.experience || '', degrees: u.degrees || '',
            bank_name: u.bank_name || '', bank_account: u.bank_account || '', ifsc: u.ifsc || '',
            upi_id: u.upi_id || '', pan_number: u.pan_number || '',
            name: u.name || '', email: u.email || '', phone: u.phone || '',
          });
          api.get(`/api/payout-config/${userId}`).then(res => {
            setPayoutConfig(res.data || { rate_30min: 0, rate_45min: 0, rate_60min: 0, rate_90min: 0, no_show_rate: 0 });
          }).catch(() => {});
          api.get(`/api/availability/${userId}`).then(res => {
            setAvailSlots(res.data.slots || []);
          }).catch(() => {});
          api.get(`/api/instructor/${userId}/scorecard`).then(res => {
            setScorecard(res.data);
          }).catch(() => {});
          // Superadmin gets full ratings with learner names
          api.get(`/api/feedback/instructor/${userId}/ratings`).then(res => {
            setInstructorRatings(res.data);
          }).catch(() => {
            // Non-superadmin fallback: no ratings detail
            setInstructorRatings(null);
          });
          // V14.5 Part 19s — pull MTD payout straight from Finance endpoint
          // so both screens agree. We use `month` (current month, IST) and
          // filter the instructor_payouts response to this user. Single
          // source of truth.
          api.get('/api/finance/instructor-payouts', { params: { month: financeMonth } })
            .then(res => {
              const all = res.data?.payouts || [];
              const mine = all.find(p => p.instructor_id === userId) || null;
              setFinancePayout(mine);
            })
            .catch(() => setFinancePayout(null));
        }
      }).finally(() => setLoading(false));
    }
  }, [open, userId]);

  // V16.3 — lazy-load the learner's submitted feedback when the tab opens
  useEffect(() => {
    if (open && userId && tab === 'feedback' && feedbackList === null) {
      api.get(`/api/users/${userId}/feedback`)
        .then((r) => setFeedbackList(r.data))
        .catch(() => setFeedbackList({ feedback: [], total: 0, avg_rating: null }));
    }
  }, [open, userId, tab, feedbackList]);

  const assignBatch = async () => {
    if (!assignBatchId) return;
    try {
      await api.post(`/api/users/${userId}/assign-batch?batch_id=${assignBatchId}`);
      toast.success('Batch assigned!');
      setAssignBatchId('');
      const res = await api.get(`/api/users/${userId}/detail`);
      setUser(res.data);
      onUpdate?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const removeBatch = async (batchId) => {
    try {
      await api.post(`/api/users/${userId}/remove-from-batch?batch_id=${batchId}`);
      toast.success('Removed from batch');
      const res = await api.get(`/api/users/${userId}/detail`);
      setUser(res.data);
      onUpdate?.();
    } catch { toast.error('Failed to remove'); }
  };

  const saveEdit = async () => {
    setEditSaving(true);
    try {
      await api.put(`/api/users/${userId}`, { name: editForm.name, email: editForm.email, phone: editForm.phone });
      toast.success('Profile updated');
      setEditMode(false);
      const res = await api.get(`/api/users/${userId}/detail`);
      setUser(res.data);
      setEditForm({ name: res.data.name || '', email: res.data.email || '', phone: res.data.phone || '' });
      onUpdate?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
    setEditSaving(false);
  };

  const savePayoutConfig = async () => {
    setPayoutSaving(true);
    try {
      await api.post('/api/payout-config', { instructor_id: userId, ...payoutConfig });
      toast.success('Payout rates saved');
      setPayoutEditing(false);
    } catch (err) { toast.error('Failed to save payout config'); }
    setPayoutSaving(false);
  };

  const saveInstructorProfile = async () => {
    setProfileSaving(true);
    try {
      await api.put(`/api/users/${userId}/instructor-profile`, profileForm);
      toast.success('Instructor profile updated');
      setProfileEditing(false);
      const res = await api.get(`/api/users/${userId}/detail`);
      const u = res.data;
      setUser(u);
      setEditForm({ name: u.name || '', email: u.email || '', phone: u.phone || '' });
      setProfileForm({
        bio: u.bio || '', experience: u.experience || '', degrees: u.degrees || '',
        bank_name: u.bank_name || '', bank_account: u.bank_account || '', ifsc: u.ifsc || '',
        upi_id: u.upi_id || '', pan_number: u.pan_number || '',
        name: u.name || '', email: u.email || '', phone: u.phone || '',
      });
      onUpdate?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update profile'); }
    setProfileSaving(false);
  };

  if (!open) return null;

  const isInstructor = user?.role === 'instructor';
  const isLearner = user?.role === 'learner';
  // V68 — Diet Communication is readable by superadmin + admin; only superadmin can watch a
  // recording (enforced server-side too).
  const isStaffViewer = ['superadmin', 'admin'].includes(currentUser?.role);

  // Group sessions by month
  const groupByMonth = (sessions) => {
    const groups = {};
    (sessions || []).forEach(s => {
      const d = new Date(s.scheduled_date + 'T00:00:00');
      const key = d.toLocaleDateString('en', { month: 'long', year: 'numeric' });
      if (!groups[key]) groups[key] = [];
      groups[key].push(s);
    });
    return groups;
  };

  // Payout stats — V14.5 Part 19s: now sourced from /api/finance/
  // instructor-payouts (same as Finance dashboard) so both screens never
  // disagree. The previous local computation undercounted because:
  //   1. past_sessions is capped at 60 (an instructor with 134 May
  //      sessions silently lost 74 from the count)
  //   2. status filter missed UK "cancelled" spelling
  //   3. no month filter — counted ALL past sessions across all time
  // The local calc is kept as a fallback for fresh-modal-open before the
  // /finance response lands.
  const fSessions = financePayout?.sessions || [];
  const usingFinance = !!financePayout;
  const payoutSessions = usingFinance
    ? fSessions
    : (user?.past_sessions?.filter(s => s.status === 'completed') || []);
  const calcRate = (dur) => {
    if (dur <= 30) return payoutConfig.rate_30min || 0;
    if (dur <= 45) return payoutConfig.rate_45min || 0;
    if (dur <= 60) return payoutConfig.rate_60min || 0;
    return payoutConfig.rate_90min || 0;
  };
  // For the Finance source, amounts already come pre-computed server-side
  // (handles adjustments, no-show rate, etc.). For the fallback, compute.
  const sessionAmount = (s) => (usingFinance ? (s.payout_amount || 0) : calcRate(s.duration_minutes || 60));
  const allPayoutAmount = usingFinance
    ? (financePayout?.total_amount || 0)
    : payoutSessions.reduce((sum, s) => sum + sessionAmount(s), 0);
  const reviewPayoutAmount = usingFinance
    ? (financePayout?.pending_amount || 0)
    : payoutSessions.filter(s => !s.payout_approved).reduce((sum, s) => sum + sessionAmount(s), 0);
  const approvedPayoutAmount = usingFinance
    ? (financePayout?.approved_amount || 0)
    : payoutSessions.filter(s => s.payout_approved).reduce((sum, s) => sum + sessionAmount(s), 0);
  const payoutsInReview = payoutSessions.filter(s => !s.payout_approved).length;
  const payoutsApproved = payoutSessions.filter(s => s.payout_approved).length;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto overflow-x-hidden p-0 [&>button]:hidden" data-testid="user-detail-modal">
        <DialogTitle className="sr-only">{user?.name ? `${user.name} - Details` : 'User Details'}</DialogTitle>
        {user && (
          <ReleasePhoneDialog open={releasePhoneOpen} user={user} onClose={() => setReleasePhoneOpen(false)}
            onReleased={(u) => { setUser(u); setEditForm({ name: u.name || '', email: u.email || '', phone: u.phone || '' }); if (onUpdate) onUpdate(); }} />
        )}
        {user && (
          <EddOverrideDialog open={eddDialogOpen} user={user} onClose={() => setEddDialogOpen(false)}
            onSaved={() => {
              api.get(`/api/users/${userId}/detail`).then((r) => setUser(r.data)).catch(() => {});
              if (onUpdate) onUpdate();
            }} />
        )}
        {loading ? (
          <div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>
        ) : user ? (
          <div>
            {/* Header */}
            <div className="p-6 pb-4 border-b">
              <div className="flex items-start gap-4 min-w-0">
                {/* V13.14.6 — derived_tier wins over learner_type so OPS-enrolled
                    learners (whose learner_type may be unset) still surface as Elite */}
                {(() => {
                  const effectiveTier = user.derived_tier || user.learner_type;
                  return (
                    <Avatar className={`h-16 w-16 ${user.role === 'learner' ? eliteAvatarRing(effectiveTier) : ''}`}>
                      {user.profile_photo_path ? (
                        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}/photo`} alt={user.name} className="h-16 w-16 rounded-full object-cover" />
                      ) : (
                        <AvatarFallback className="text-lg bg-slate-800 text-white">{getInitials(user.name)}</AvatarFallback>
                      )}
                    </Avatar>
                  );
                })()}
                <div className="flex-1 min-w-0">
                  {editMode ? (
                    <div className="space-y-2">
                      <Input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} placeholder="Full name" className="h-9" />
                      <div className="grid grid-cols-2 gap-2">
                        <Input value={editForm.email} onChange={e => setEditForm({...editForm, email: e.target.value})} placeholder="Email" className="h-9 text-sm" />
                        <Input value={editForm.phone} onChange={e => setEditForm({...editForm, phone: e.target.value})} placeholder="Phone" className="h-9 text-sm" />
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={saveEdit} disabled={editSaving}>
                          {editSaving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3 w-3 mr-1" />}Save
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditMode(false)}>Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        <h2 className="font-heading text-2xl font-semibold">{user.name}</h2>
                        <Badge variant="outline" className="capitalize">{user.role}</Badge>
                        {user.role === 'learner' && (
                          <TierBadge tier={user.derived_tier || user.learner_type} data-testid="user-detail-tier-badge" />
                        )}
                        {user.is_suspended && <Badge variant="outline" className="bg-rose-50 text-rose-800 border-rose-200">Suspended</Badge>}
                        <Button variant="ghost" size="icon" className="h-7 w-7 ml-1" onClick={() => setEditMode(true)} title="Edit profile">
                          <Pencil className="h-3 w-3" />
                        </Button>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground flex-wrap">
                        <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />Joined {formatDateTime(user.created_at)}</span>
                        {user.phone && (
                          <span className="flex items-center gap-1" data-testid="user-detail-phone">
                            <Phone className="h-3 w-3" />{user.phone}
                            {user.phone_verified && <ShieldCheck className="h-3 w-3 text-emerald-600" title="Verified" data-testid="user-phone-verified" />}
                            {user.role === 'learner' && (
                              <button type="button" className="text-[11px] underline underline-offset-2 text-rose-600 hover:text-rose-700 ml-1"
                                onClick={() => setReleasePhoneOpen(true)} data-testid="release-phone-btn">Release</button>
                            )}
                          </span>
                        )}
                        <span className="flex items-center gap-1 min-w-0 max-w-full"><Mail className="h-3 w-3 shrink-0" /><span className="truncate" title={user.email}>{user.email}</span></span>
                        {isLearner && (
                          <span className="flex items-center gap-1" data-testid="user-detail-edd">
                            <Calendar className="h-3 w-3" />
                            {user.pregnancy_profile?.delivery_date
                              ? <>Delivered {formatDate(user.pregnancy_profile.delivery_date)}</>
                              : <>Due {user.pregnancy_profile?.edd ? formatDate(user.pregnancy_profile.edd) : '— not set'}</>}
                            {user.pregnancy_profile?.edd_locked_at && <span title="Locked — learner cannot edit">🔒</span>}
                            <button type="button" data-testid="header-change-edd-btn"
                              className="text-[11px] underline underline-offset-2 text-blue-600 hover:text-blue-700 ml-1"
                              onClick={() => setEddDialogOpen(true)}>Change</button>
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </div>
                <Button variant="ghost" size="icon" onClick={onClose}><X className="h-4 w-4" /></Button>
              </div>
            </div>

            {/* Tabs */}
            <div className="p-6 pt-4">
              <Tabs value={tab} onValueChange={setTab}>
                <div className="flex items-center justify-between mb-4">
                  <TabsList className="flex-wrap h-auto max-w-full">
                    <TabsTrigger value="batches">Batches</TabsTrigger>
                    <TabsTrigger value="sessions">Sessions</TabsTrigger>
                    {isLearner && <TabsTrigger value="attendance" data-testid="learner-attendance-tab">Attendance</TabsTrigger>}
                    {isLearner && <TabsTrigger value="fees">Fees</TabsTrigger>}
                    {isLearner && <TabsTrigger value="courses" data-testid="learner-courses-tab">Courses</TabsTrigger>}
                    {isInstructor && <TabsTrigger value="profile" data-testid="instructor-profile-tab">Profile</TabsTrigger>}
                    {isInstructor && <TabsTrigger value="scorecard" data-testid="instructor-scorecard-tab">Scorecard</TabsTrigger>}
                    {isInstructor && <TabsTrigger value="payouts">MTD Payouts</TabsTrigger>}
                    {isInstructor && <TabsTrigger value="working_hours">Working Hours</TabsTrigger>}
                    {isLearner && <TabsTrigger value="communications">Communications</TabsTrigger>}
                    {isLearner && <TabsTrigger value="feedback" data-testid="learner-feedback-tab">Feedback</TabsTrigger>}
                    {isLearner && <TabsTrigger value="soulai" data-testid="learner-soulai-tab">SoulAI Usage</TabsTrigger>}
                    {isLearner && isStaffViewer && <TabsTrigger value="diet_comms" data-testid="learner-diet-comms-tab">Diet Communication</TabsTrigger>}
                  </TabsList>
                  {/* Only show Add Batch for learners */}
                  {tab === 'batches' && isLearner && (
                    <div className="flex items-center gap-2">
                      <Select value={assignBatchId} onValueChange={setAssignBatchId} onOpenChange={(o) => o && loadAssignableBatches()}>
                        <SelectTrigger className="w-48 text-xs h-8" data-testid="assign-batch-select"><SelectValue placeholder="Select batch" /></SelectTrigger>
                        <SelectContent position="popper" sideOffset={4} className="z-[300]">
                          {allBatches === null || (allBatches.length === 0) ? (
                            <div className="px-2 py-1.5 text-xs text-muted-foreground">Loading batches…</div>
                          ) : allBatches.filter(b => !user.batches?.some(ub => ub.id === b.id)).map(b => (
                            <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button size="sm" onClick={assignBatch} disabled={!assignBatchId}><Plus className="h-3 w-3 mr-1" />Add</Button>
                    </div>
                  )}
                </div>

                {/* Batches Tab */}
                <TabsContent value="batches">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {isLearner && (
                      <div className="space-y-3">
                        <CountrySignalsCard userId={userId} />
                        <div className="p-4 rounded-xl border">
                          <h4 className="text-sm font-medium mb-2">Pregnancy Details</h4>
                          <div className="space-y-1.5 text-sm">
                            {user.pregnancy_profile?.edd && <p><span className="text-muted-foreground">EDD:</span> {formatDate(user.pregnancy_profile.edd)}</p>}
                            {user.pregnancy_profile?.edd_locked_at && <p className="text-[11px] text-amber-700">🔒 Locked {user.pregnancy_profile.edd_source ? `(${user.pregnancy_profile.edd_source.replace(/_/g, ' ')})` : ''}</p>}
                            {(() => {
                              const pp = user.pregnancy_profile || {};
                              // V61.1 — trust the backend SSOT (`derived_stage`); the raw
                              // course_type/stage mirrors can be stale after an EDD correction.
                              const isPostnatalProfile = user.derived_stage
                                ? user.derived_stage === 'postnatal'
                                : (pp.course_type === 'postnatal' || pp.stage === 'postnatal' || !!pp.delivery_date);
                              if (isPostnatalProfile) {
                                // V16.3 — postnatal users no longer show stale "Week 40 / Trimester 3";
                                // show delivered status + weeks postpartum instead.
                                const ref = pp.delivery_date || pp.edd;
                                let wk = null;
                                if (ref) {
                                  const d = new Date(ref);
                                  if (!Number.isNaN(d.getTime())) {
                                    wk = Math.max(0, Math.floor((Date.now() - d.getTime()) / (7 * 24 * 60 * 60 * 1000)));
                                  }
                                }
                                return (
                                  <div className="text-sm" data-testid="postnatal-status-row">
                                    <span className="text-muted-foreground">Stage:</span>{' '}
                                    <Badge variant="outline" className="bg-pink-50 text-pink-700 border-pink-200">
                                      Delivered{wk !== null && wk > 0 ? ` · ${wk} wk${wk === 1 ? '' : 's'} postpartum` : ''}
                                    </Badge>
                                    {!pp.delivery_date && (
                                      <span className="text-[11px] text-muted-foreground ml-1.5">(estimated from EDD)</span>
                                    )}
                                    <div className="mt-1" data-testid="doctor-clearance-row">
                                      <span className="text-muted-foreground">Doctor clearance:</span>{' '}
                                      {pp.doctor_clearance === true && <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Cleared for gentle exercise</Badge>}
                                      {pp.doctor_clearance === false && <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200">Not yet cleared</Badge>}
                                      {pp.doctor_clearance == null && <span className="text-[11px] text-muted-foreground">not asked</span>}
                                    </div>
                                  </div>
                                );
                              }
                              return (
                                <>
                                  {pp.trimester && <p><span className="text-muted-foreground">Trimester:</span> {pp.trimester}</p>}
                                  {(() => {
                                    const eddVal = pp.edd;
                                    if (!eddVal) return null;
                                    const today = new Date(); today.setHours(0, 0, 0, 0);
                                    const eddDate = new Date(eddVal);
                                    if (Number.isNaN(eddDate.getTime())) return null;
                                    const daysToEdd = Math.round((eddDate - today) / (1000 * 60 * 60 * 24));
                                    const weeksRemaining = Math.max(0, Math.floor(daysToEdd / 7));
                                    const week = Math.max(1, Math.min(42, 40 - weeksRemaining));
                                    return (
                                      <div className="text-sm" data-testid="pregnancy-current-week">
                                        <span className="text-muted-foreground">Current Week:</span>{' '}
                                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Week {week}</Badge>
                                      </div>
                                    );
                                  })()}
                                </>
                              );
                            })()}
                            {user.pregnancy_profile?.risk_level && <div><span className="text-muted-foreground">Risk:</span> <Badge variant="outline" className={user.pregnancy_profile.risk_level === 'high' ? 'bg-rose-50 text-rose-800' : 'bg-emerald-50 text-emerald-800'}>{user.pregnancy_profile.risk_level}</Badge></div>}
                            {user.pregnancy_profile?.course_type && <p><span className="text-muted-foreground">Course:</span> {user.pregnancy_profile.course_type}</p>}
                            {user.pregnancy_profile?.delivery_date && (
                              <p data-testid="pregnancy-delivery-date">
                                <span className="text-muted-foreground">Delivery date:</span> {formatDate(user.pregnancy_profile.delivery_date)}
                              </p>
                            )}
                            {user.pregnancy_profile?.delivery_mode && (
                              <div data-testid="pregnancy-delivery-mode">
                                <span className="text-muted-foreground">Delivery mode:</span>{' '}
                                <Badge
                                  variant="outline"
                                  className={(user.pregnancy_profile.delivery_mode === 'vaginal' || user.pregnancy_profile.delivery_mode === 'natural')
                                    ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                                    : 'bg-rose-50 text-rose-800 border-rose-200'}
                                >
                                  {(user.pregnancy_profile.delivery_mode === 'vaginal' || user.pregnancy_profile.delivery_mode === 'natural') ? 'Vaginal birth' : 'C-section'}
                                </Badge>
                              </div>
                            )}
                            {user.pending_postnatal_onboarding && (
                              <p className="text-[11px] text-amber-700 mt-1" data-testid="pending-postnatal-onboarding-flag">
                                ⏳ Pending postnatal onboarding (learner will be prompted on next login)
                              </p>
                            )}
                          </div>
                          <AdminEddOverrideControl userId={user.id} currentEdd={user.pregnancy_profile?.edd} onChanged={onUpdate} />
                          <AdminPostnatalUnlockControl userId={user.id}
                            unlocked={user.pregnancy_profile?.postnatal_flip_unlocked_by_admin}
                            onChanged={onUpdate} />
                          <AdminRevertPostnatalControl userId={user.id}
                            courseType={user.pregnancy_profile?.course_type}
                            flippedVia={user.stage_flipped_via}
                            onChanged={onUpdate} />
                          <MembershipExpiryCard userId={user.id} />
                          <PrenatalCoachAccessControl userId={user.id} />
                          <ProductAccessControl userId={user.id} />
                          <StoreSubscriptionsCard userId={user.id} isSuperadmin={isSuperadmin} />
                          {user.pregnancy_profile?.pregnancy_notes && (
                            <div className="mt-3 pt-3 border-t">
                              <p className="text-xs text-muted-foreground mb-1">Notes</p>
                              <p className="text-sm">{user.pregnancy_profile.pregnancy_notes}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    <div className={isLearner ? 'lg:col-span-2' : 'lg:col-span-3'}>
                      {user.batches?.length === 0 ? (
                        <p className="text-center text-muted-foreground py-8">
                          {isInstructor ? 'Batches will appear here when sessions are scheduled for this instructor.' : 'No batches assigned'}
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {user.batches?.map(b => (
                            <div key={b.id} className="flex items-center justify-between p-3 rounded-xl border hover:bg-muted/30 transition-colors" data-testid={`user-batch-row-${b.id}`}>
                              <div>
                                <p className="text-sm font-medium">{b.name}</p>
                                <p className="text-xs text-muted-foreground capitalize">{b.course_type} {b.batch_type === 'one_on_one' ? '· 1:1' : '· Group'}</p>
                                {b.assigned_at && (
                                  <p className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1" data-testid={`user-batch-assigned-${b.id}`}>
                                    <Calendar className="h-3 w-3" />
                                    Assigned on {formatDate(b.assigned_at)}{b.assigned_by ? ` · by ${String(b.assigned_by).replace(/_/g, ' ')}` : ''}
                                  </p>
                                )}
                              </div>
                              {isLearner && (
                                <Button variant="ghost" size="sm" className="text-destructive" onClick={() => removeBatch(b.id)}>
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </TabsContent>

                {/* Sessions Tab — Wise-style. V14.5 Part 18d: three tabs
                    (Upcoming / Attended / Past). "Past" shows EVERY past
                    session in the learner's enrolled batches after their
                    LMS join date, regardless of booking — admins use this
                    to audit absences. "Attended" filters past sessions to
                    only the ones where the learner appears in attendance[].
                    V14.5 Part 19s — for INSTRUCTORS, the Attended tab makes
                    no sense (they teach, not attend) and Past is a duplicate
                    of the learner-shaped vocabulary. So instructors get a
                    cleaner header: "Schedule" + a stat strip showing today /
                    this week / upcoming counts + a "Next session" banner
                    when the gap is > 7 days (founder hit the confusion when
                    Madhumitha's next session was 6 months away — the modal
                    showed Nov 30 with no context).
                 */}
                <TabsContent value="sessions">
                  {user.role === 'instructor' ? (
                    <>
                      {/* Stat strip — instructors */}
                      <div className="grid grid-cols-3 gap-3 mb-4" data-testid="instructor-schedule-stats">
                        <div className="rounded-lg border bg-card p-3">
                          <p className="text-[11px] text-muted-foreground uppercase tracking-wide">Today</p>
                          <p className="text-2xl font-semibold">{user.schedule_meta?.today_count ?? 0}</p>
                          <p className="text-[11px] text-muted-foreground">sessions</p>
                        </div>
                        <div className="rounded-lg border bg-card p-3">
                          <p className="text-[11px] text-muted-foreground uppercase tracking-wide">This week</p>
                          <p className="text-2xl font-semibold">{user.schedule_meta?.this_week_count ?? 0}</p>
                          <p className="text-[11px] text-muted-foreground">Mon–Sun (IST)</p>
                        </div>
                        <div className="rounded-lg border bg-card p-3">
                          <p className="text-[11px] text-muted-foreground uppercase tracking-wide">Upcoming total</p>
                          <p className="text-2xl font-semibold">{user.schedule_meta?.total_upcoming ?? 0}</p>
                          <p className="text-[11px] text-muted-foreground">all future</p>
                        </div>
                      </div>

                      {/* Gap-warning banner */}
                      {user.schedule_meta?.next_session_days_away != null && user.schedule_meta.next_session_days_away > 7 && (
                        <div className="rounded-lg border border-amber-500/50 bg-amber-500/10 p-3 mb-4 text-sm" data-testid="next-session-gap-banner">
                          <span className="font-semibold">Next session:</span>{' '}
                          {user.schedule_meta.next_session_date} at {user.schedule_meta.next_session_time}{' '}
                          <span className="text-muted-foreground">
                            ({user.schedule_meta.next_session_days_away} days from now)
                          </span>
                          <span className="block text-xs text-muted-foreground mt-1">
                            Nothing scheduled before then. If you expected sooner sessions, check whether the instructor has been assigned to current batches.
                          </span>
                        </div>
                      )}

                      <div className="flex items-center justify-between mb-3">
                        <div className="flex gap-2 flex-wrap">
                          <Button variant={sessionView === 'upcoming' ? 'default' : 'outline'} size="sm" onClick={() => setSessionView('upcoming')} data-testid="sessions-tab-upcoming">
                            Schedule <Badge variant="secondary" className="ml-1 text-[10px]">{user.upcoming_sessions?.length || 0}</Badge>
                          </Button>
                          <Button variant={sessionView === 'history' ? 'default' : 'outline'} size="sm" onClick={() => setSessionView('history')} data-testid="sessions-tab-history">
                            History <Badge variant="secondary" className="ml-1 text-[10px]">{user.past_sessions?.length || 0}</Badge>
                          </Button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex gap-2 flex-wrap">
                        <Button variant={sessionView === 'upcoming' ? 'default' : 'outline'} size="sm" onClick={() => setSessionView('upcoming')} data-testid="sessions-tab-upcoming">
                          Upcoming <Badge variant="secondary" className="ml-1 text-[10px]">{user.upcoming_sessions?.length || 0}</Badge>
                        </Button>
                        <Button variant={sessionView === 'attended' ? 'default' : 'outline'} size="sm" onClick={() => setSessionView('attended')} data-testid="sessions-tab-attended">
                          Attended <Badge variant="secondary" className="ml-1 text-[10px]">{(user.past_sessions || []).filter(s => s.was_attended).length}</Badge>
                        </Button>
                        <Button variant={sessionView === 'past' ? 'default' : 'outline'} size="sm" onClick={() => setSessionView('past')} data-testid="sessions-tab-past">
                          Past <Badge variant="secondary" className="ml-1 text-[10px]">{user.past_sessions?.length || 0}</Badge>
                        </Button>
                      </div>
                      {sessionView !== 'upcoming' && (
                        <select value={sessionMonth} onChange={(e) => setSessionMonth(e.target.value)}
                          className="h-8 rounded-md border border-border bg-background px-2 text-xs" data-testid="sessions-month-filter">
                          <option value="all">All months</option>
                          {[...new Set((user.past_sessions || []).map(s => (s.scheduled_date || '').slice(0, 7)).filter(Boolean))].map(m => (
                            <option key={m} value={m}>{new Date(`${m}-01T00:00:00`).toLocaleDateString('en', { month: 'long', year: 'numeric' })}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}

                  <div className="space-y-4 max-h-[400px] overflow-y-auto scrollbar-thin">
                    {(() => {
                      const inMonth = (s) => isLearner === false || sessionMonth === 'all' || (s.scheduled_date || '').startsWith(sessionMonth);
                      const sessionsForView = sessionView === 'upcoming'
                        ? (user.upcoming_sessions || [])
                        : sessionView === 'attended'
                          ? (user.past_sessions || []).filter(s => s.was_attended && inMonth(s))
                          : (user.past_sessions || []).filter(inMonth);   // 'past' OR 'history' (instructor)
                      return Object.entries(groupByMonth(sessionsForView)).map(([month, sessions]) => (
                      <div key={month}>
                        <div className="bg-muted/50 px-3 py-1.5 rounded-lg mb-2">
                          <p className="text-xs font-medium text-muted-foreground">{month}</p>
                        </div>
                        <div className="space-y-2">
                          {sessions.map(s => {
                            const d = new Date(s.scheduled_date + 'T00:00:00');
                            const localStart = isIST() ? s.scheduled_time : convertISTToLocal(s.scheduled_time, s.scheduled_date);
                            const endTime = (() => {
                              const localTime = localStart;
                              const [h, m] = (localTime || '0:0').split(':').map(Number);
                              const endMin = h * 60 + m + (s.duration_minutes || 60);
                              const eh = Math.floor(endMin / 60) % 24;
                              const em = endMin % 60;
                              return `${eh > 12 ? eh - 12 : eh || 12}:${String(em).padStart(2, '0')} ${eh >= 12 ? 'PM' : 'AM'}`;
                            })();
                            // Check if session is starting soon (using admin-configured buffer)
                            const isStartingSoon = (() => {
                              try {
                                const sessionDt = new Date(`${s.scheduled_date}T${s.scheduled_time}:00`);
                                const diffMin = (sessionDt - new Date()) / (1000 * 60);
                                const buffer = isInstructor ? bufferSettings.instructor_start_buffer : bufferSettings.learner_join_buffer;
                                return diffMin <= buffer;
                              } catch { return false; }
                            })();
                            const showJoinButtons = s.status === 'live' || (s.status === 'upcoming' && isStartingSoon);
                            return (
                              <div key={s.id} className="flex items-center justify-between p-3 rounded-xl border hover:bg-muted/20 transition-colors">
                                <div className="flex items-center gap-3">
                                  <div className="text-center min-w-[44px] bg-primary/10 rounded-lg px-2 py-1">
                                    <p className="text-[10px] text-primary uppercase">{d.toLocaleDateString('en', { month: 'short' })}</p>
                                    <p className="text-lg font-semibold text-primary leading-tight">{d.getDate()}</p>
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium">{s.batch_name || s.title}, {s.batch_type === 'one_on_one' ? '1:1' : 'Group'}</p>
                                    <p className="text-xs text-muted-foreground flex items-center gap-1"><Video className="h-3 w-3" />Live Session</p>
                                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                                      <Clock className="h-3 w-3" />
                                      {d.toLocaleDateString('en', { weekday: 'short' })} {formatTime(localStart)} - {endTime}
                                    </p>
                                    <Badge variant="outline" className={`${getStatusColor(s.status)} mt-1 text-[10px]`}>{s.status}</Badge>
                                    {/* V70.1 — the Past list deliberately shows EVERY session of
                                        her batch (founder: admins want to see skipped classes too),
                                        so each row must say whether SHE booked it. Without this, a
                                        batch class she never booked reads as her booking — which is
                                        exactly how a 6:30 PM class was mistaken for her 7:30 PM one. */}
                                    {isLearner && sessionView !== 'upcoming' && (
                                      s.was_booked ? (
                                        <Badge variant="outline" className="mt-1 ml-1 text-[10px] border-emerald-300 text-emerald-700" data-testid={`session-booked-${s.id}`}>booked by her</Badge>
                                      ) : (
                                        <Badge variant="outline" className="mt-1 ml-1 text-[10px] border-muted text-muted-foreground" data-testid={`session-notbooked-${s.id}`}>not booked · batch class</Badge>
                                      )
                                    )}
                                    {isLearner && s.was_attended && (
                                      <Badge variant="outline" className="mt-1 ml-1 text-[10px] border-sky-300 text-sky-700" data-testid={`session-attended-${s.id}`}>attended</Badge>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {showJoinButtons && s.zoom_start_url && isInstructor && (
                                    <Button size="sm" onClick={() => window.open(s.zoom_start_url, '_blank')}>
                                      <Video className="h-3 w-3 mr-1" />Start
                                    </Button>
                                  )}
                                  {showJoinButtons && s.zoom_join_url && isLearner && (
                                    <Button size="sm" onClick={() => window.open(s.zoom_join_url, '_blank')}>
                                      <Video className="h-3 w-3 mr-1" />Join
                                    </Button>
                                  )}
                                  {s.status === 'upcoming' && (
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" size="icon" className="h-7 w-7"><MoreVertical className="h-4 w-4" /></Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end">
                                        <DropdownMenuItem onClick={() => { onClose(); window.location.href = `/admin/batches/${s.batch_id}/edit-session?session=${s.id}&scope=this_session`; }}>
                                          <Pencil className="h-4 w-4 mr-2" />Edit Session
                                        </DropdownMenuItem>
                                        <DropdownMenuItem className="text-destructive" onClick={async () => {
                                          if (window.confirm('Delete this session?')) {
                                            try { await api.delete(`/api/sessions/${s.id}`); toast.success('Deleted'); const res = await api.get(`/api/users/${userId}/detail`); setUser(res.data); } catch { toast.error('Failed'); }
                                          }
                                        }}>
                                          <Trash2 className="h-4 w-4 mr-2" />Delete
                                        </DropdownMenuItem>
                                        <DropdownMenuItem className="text-destructive" onClick={async () => {
                                          if (window.confirm('Cancel this session?')) {
                                            try { await api.post(`/api/sessions/${s.id}/cancel?reason=Canceled by admin`); toast.success('Canceled'); const res = await api.get(`/api/users/${userId}/detail`); setUser(res.data); } catch { toast.error('Failed'); }
                                          }
                                        }}>
                                          <XCircle className="h-4 w-4 mr-2" />Cancel
                                        </DropdownMenuItem>
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ));
                    })()}
                    {(() => {
                      const inMonth = (s) => isLearner === false || sessionMonth === 'all' || (s.scheduled_date || '').startsWith(sessionMonth);
                      const len = sessionView === 'upcoming'
                        ? (user.upcoming_sessions || []).length
                        : sessionView === 'attended'
                          ? (user.past_sessions || []).filter(s => s.was_attended && inMonth(s)).length
                          : (user.past_sessions || []).filter(inMonth).length;
                      return len === 0 ? (
                        <p className="text-center text-muted-foreground py-6" data-testid={`sessions-empty-${sessionView}`}>No {sessionView} sessions</p>
                      ) : null;
                    })()}
                  </div>
                </TabsContent>

                {/* Learner Attendance Tab */}
                <TabsContent value="attendance">
                  <div data-testid="learner-attendance-content">
                    {/* Summary KPI Cards */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                      <div className="p-3 rounded-xl border bg-primary/5">
                        <p className="text-xs text-muted-foreground">Booked (Past)</p>
                        <p className="text-xl font-semibold" data-testid="attendance-booked-past">{user.booked_past || 0}</p>
                      </div>
                      <div className="p-3 rounded-xl border">
                        <p className="text-xs text-muted-foreground">Attended</p>
                        <p className="text-xl font-semibold" data-testid="attendance-attended">{user.attended_sessions || 0}</p>
                      </div>
                      <div className="p-3 rounded-xl border">
                        <div className="flex items-center gap-1">
                          <p className="text-xs text-muted-foreground">Attendance %</p>
                          <TooltipProvider delayDuration={100}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button type="button" aria-label="Attendance definition" className="text-muted-foreground/70 hover:text-primary transition-colors" data-testid="admin-attendance-info-btn">
                                  <Info className="h-3.5 w-3.5" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-[260px] text-left leading-relaxed" data-testid="admin-attendance-info-tooltip">
                                Attendance % = classes attended ÷ classes booked, across all the learner&apos;s batches. Only past (conducted) classes count toward this. Classes the learner didn&apos;t book aren&apos;t counted.
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <p className={`text-xl font-semibold ${(user.attendance_pct || 0) >= 80 ? 'text-emerald-600' : (user.attendance_pct || 0) >= 50 ? 'text-amber-600' : 'text-rose-600'}`} data-testid="attendance-pct">{user.attendance_pct || 0}%</p>
                      </div>
                      <div className="p-3 rounded-xl border">
                        <p className="text-xs text-muted-foreground">Booked (Upcoming)</p>
                        <p className="text-xl font-semibold" data-testid="attendance-booked-upcoming">{user.booked_upcoming || 0}</p>
                      </div>
                    </div>

                    {/* Detailed Session-by-Session List */}
                    <h4 className="text-sm font-semibold mb-3">Past Sessions — Booking & Attendance</h4>
                    <div className="space-y-2 max-h-[350px] overflow-y-auto scrollbar-thin">
                      {(() => {
                        const bookedPastSessions = (user.past_sessions || []).filter(s => s.was_booked);
                        return bookedPastSessions.length > 0 ? bookedPastSessions.map(s => {
                        const d = new Date(s.scheduled_date + 'T00:00:00');
                        return (
                          <div key={s.id} className="flex items-center justify-between p-2.5 rounded-lg border hover:bg-muted/20 transition-colors" data-testid={`attendance-row-${s.id}`}>
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="text-center min-w-[36px]">
                                <p className="text-[9px] text-muted-foreground uppercase">{d.toLocaleDateString('en', { month: 'short' })}</p>
                                <p className="text-sm font-semibold leading-tight">{d.getDate()}</p>
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-medium truncate">{s.batch_name || s.title}</p>
                                <p className="text-xs text-muted-foreground">{formatTime(s.scheduled_time)} &middot; {s.duration_minutes}m</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              {s.was_attended ? (
                                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">Attended</Badge>
                              ) : (
                                <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 text-[10px]">Absent</Badge>
                              )}
                            </div>
                          </div>
                        );
                      }) : (
                          <p className="text-center text-muted-foreground py-6">No booked sessions found</p>
                        );
                      })()}
                    </div>

                    {/* Upcoming Bookings */}
                    {user.upcoming_sessions?.some(s => s.was_booked) && (
                      <div className="mt-5">
                        <h4 className="text-sm font-semibold mb-3">Upcoming Booked Sessions</h4>
                        <div className="space-y-2">
                          {user.upcoming_sessions.filter(s => s.was_booked).map(s => {
                            const d = new Date(s.scheduled_date + 'T00:00:00');
                            return (
                              <div key={s.id} className="flex items-center justify-between p-2.5 rounded-lg border bg-blue-50/30">
                                <div className="flex items-center gap-3">
                                  <div className="text-center min-w-[36px]">
                                    <p className="text-[9px] text-primary uppercase">{d.toLocaleDateString('en', { month: 'short' })}</p>
                                    <p className="text-sm font-semibold text-primary leading-tight">{d.getDate()}</p>
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium">{s.batch_name || s.title}</p>
                                    <p className="text-xs text-muted-foreground">{formatTime(s.scheduled_time)} &middot; {s.duration_minutes}m</p>
                                  </div>
                                </div>
                                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-[10px]">Booked</Badge>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* Fees Tab (Learner only) — V13.14.6 */}
                <TabsContent value="fees">
                  <FeesPanel userId={user?.id} userName={user?.name} />
                </TabsContent>

                {/* Recorded Courses Tab — V13.6 (assignment to learners) */}
                <TabsContent value="courses">
                  <CoursesTabPanel userId={user?.id} userName={user?.name} />
                </TabsContent>

                {/* Instructor Profile Tab */}
                <TabsContent value="profile">
                  <div data-testid="instructor-profile-content">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-heading text-lg font-semibold">Instructor Profile</h4>
                      {isSuperadmin && !profileEditing && (
                        <Button variant="outline" size="sm" onClick={() => setProfileEditing(true)} data-testid="edit-instructor-profile-btn">
                          <Pencil className="h-3 w-3 mr-1" />Edit Profile
                        </Button>
                      )}
                      {profileEditing && (
                        <div className="flex gap-2">
                          <Button size="sm" onClick={saveInstructorProfile} disabled={profileSaving} data-testid="save-instructor-profile-btn">
                            {profileSaving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3 w-3 mr-1" />}Save
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => { setProfileEditing(false); setProfileForm({ bio: user?.bio || '', experience: user?.experience || '', degrees: user?.degrees || '', bank_name: user?.bank_name || '', bank_account: user?.bank_account || '', ifsc: user?.ifsc || '', upi_id: user?.upi_id || '', pan_number: user?.pan_number || '', name: user?.name || '', email: user?.email || '', phone: user?.phone || '' }); }}>
                            Cancel
                          </Button>
                        </div>
                      )}
                    </div>

                    <div className="space-y-5">
                      {/* Contact Info (Superadmin only can edit) */}
                      {isSuperadmin && (
                        <div className="p-4 rounded-xl border bg-muted/30">
                          <h5 className="text-sm font-semibold mb-3 flex items-center gap-2"><UserIcon className="h-4 w-4 text-primary" />Contact Details</h5>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div>
                              <label className="text-xs text-muted-foreground mb-1 block">Full Name</label>
                              {profileEditing ? (
                                <Input value={profileForm.name} onChange={e => setProfileForm({...profileForm, name: e.target.value})} className="h-9" data-testid="instructor-name-input" />
                              ) : (
                                <p className="text-sm font-medium" data-testid="instructor-name-display">{user?.name || '-'}</p>
                              )}
                            </div>
                            <div>
                              <label className="text-xs text-muted-foreground mb-1 block">Email</label>
                              {profileEditing ? (
                                <Input value={profileForm.email} onChange={e => setProfileForm({...profileForm, email: e.target.value})} className="h-9" data-testid="instructor-email-input" />
                              ) : (
                                <p className="text-sm font-medium">{user?.email || '-'}</p>
                              )}
                            </div>
                            <div>
                              <label className="text-xs text-muted-foreground mb-1 block">Phone</label>
                              {profileEditing ? (
                                <Input value={profileForm.phone} onChange={e => setProfileForm({...profileForm, phone: e.target.value})} className="h-9" data-testid="instructor-phone-input" />
                              ) : (
                                <p className="text-sm font-medium">{user?.phone || '-'}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Bio & Experience */}
                      <div className="p-4 rounded-xl border bg-muted/30">
                        <h5 className="text-sm font-semibold mb-3 flex items-center gap-2"><GraduationCap className="h-4 w-4 text-primary" />About & Qualifications</h5>
                        <div className="space-y-3">
                          <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Bio</label>
                            {profileEditing ? (
                              <Textarea value={profileForm.bio} onChange={e => setProfileForm({...profileForm, bio: e.target.value})} rows={3} placeholder="Instructor bio..." data-testid="instructor-bio-input" />
                            ) : (
                              <p className="text-sm" data-testid="instructor-bio-display">{user?.bio || <span className="text-muted-foreground italic">No bio added</span>}</p>
                            )}
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs text-muted-foreground mb-1 block">Experience</label>
                              {profileEditing ? (
                                <Input value={profileForm.experience} onChange={e => setProfileForm({...profileForm, experience: e.target.value})} placeholder="e.g. 5 years in prenatal yoga" className="h-9" data-testid="instructor-experience-input" />
                              ) : (
                                <p className="text-sm" data-testid="instructor-experience-display">{user?.experience || <span className="text-muted-foreground italic">Not set</span>}</p>
                              )}
                            </div>
                            <div>
                              <label className="text-xs text-muted-foreground mb-1 block">Degrees / Certifications</label>
                              {profileEditing ? (
                                <Input value={profileForm.degrees} onChange={e => setProfileForm({...profileForm, degrees: e.target.value})} placeholder="e.g. MSc Yoga, RPYT-85" className="h-9" data-testid="instructor-degrees-input" />
                              ) : (
                                <p className="text-sm" data-testid="instructor-degrees-display">{user?.degrees || <span className="text-muted-foreground italic">Not set</span>}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Bank & Financial Details */}
                      <div className="p-4 rounded-xl border bg-muted/30">
                        <h5 className="text-sm font-semibold mb-3 flex items-center gap-2"><Landmark className="h-4 w-4 text-primary" />Bank & Financial Details</h5>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Bank Name</label>
                            {profileEditing ? (
                              <Input value={profileForm.bank_name} onChange={e => setProfileForm({...profileForm, bank_name: e.target.value})} placeholder="Bank name" className="h-9" data-testid="instructor-bank-name-input" />
                            ) : (
                              <p className="text-sm font-medium" data-testid="instructor-bank-name-display">{user?.bank_name || <span className="text-muted-foreground italic">Not set</span>}</p>
                            )}
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Account Number</label>
                            {profileEditing ? (
                              <Input value={profileForm.bank_account} onChange={e => setProfileForm({...profileForm, bank_account: e.target.value})} placeholder="Account number" className="h-9" data-testid="instructor-bank-account-input" />
                            ) : (
                              <p className="text-sm font-mono" data-testid="instructor-bank-account-display">{user?.bank_account ? `****${user.bank_account.slice(-4)}` : <span className="text-muted-foreground italic font-sans">Not set</span>}</p>
                            )}
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground mb-1 block">IFSC Code</label>
                            {profileEditing ? (
                              <Input value={profileForm.ifsc} onChange={e => setProfileForm({...profileForm, ifsc: e.target.value})} placeholder="IFSC code" className="h-9" data-testid="instructor-ifsc-input" />
                            ) : (
                              <p className="text-sm font-mono" data-testid="instructor-ifsc-display">{user?.ifsc || <span className="text-muted-foreground italic font-sans">Not set</span>}</p>
                            )}
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground mb-1 block">UPI ID</label>
                            {profileEditing ? (
                              <Input value={profileForm.upi_id} onChange={e => setProfileForm({...profileForm, upi_id: e.target.value})} placeholder="UPI ID" className="h-9" data-testid="instructor-upi-input" />
                            ) : (
                              <p className="text-sm" data-testid="instructor-upi-display">{user?.upi_id || <span className="text-muted-foreground italic">Not set</span>}</p>
                            )}
                          </div>
                        </div>
                        <div className="mt-3">
                          <label className="text-xs text-muted-foreground mb-1 block">PAN Number</label>
                          {profileEditing ? (
                            <Input value={profileForm.pan_number} onChange={e => setProfileForm({...profileForm, pan_number: e.target.value.toUpperCase()})} placeholder="PAN number" className="h-9 w-48 uppercase" maxLength={10} data-testid="instructor-pan-input" />
                          ) : (
                            <p className="text-sm font-mono" data-testid="instructor-pan-display">{user?.pan_number ? `${user.pan_number.slice(0,2)}****${user.pan_number.slice(-2)}` : <span className="text-muted-foreground italic font-sans">Not set</span>}</p>
                          )}
                        </div>
                      </div>

                      {!isSuperadmin && (
                        <p className="text-xs text-muted-foreground text-center py-2">Only superadmins can edit instructor profile details.</p>
                      )}
                    </div>
                  </div>
                </TabsContent>

                {/* Instructor Scorecard Tab */}
                <TabsContent value="scorecard">
                  <div data-testid="instructor-scorecard-content">
                    {scorecard ? (
                      <div className="space-y-5">
                        {/* KPI Cards */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          <div className="p-4 rounded-xl border bg-primary/5 transition-shadow hover:shadow-[var(--shadow-md)]">
                            <div className="flex items-center gap-2 mb-2">
                              <BookOpen className="h-4 w-4 text-primary" />
                              <p className="text-xs text-muted-foreground">Total Sessions</p>
                            </div>
                            <p className="text-2xl font-semibold" data-testid="scorecard-total-sessions">{scorecard.total_sessions}</p>
                          </div>
                          <div className="p-4 rounded-xl border transition-shadow hover:shadow-[var(--shadow-md)]">
                            <div className="flex items-center gap-2 mb-2">
                              <Clock className="h-4 w-4 text-teal-600" />
                              <p className="text-xs text-muted-foreground">Hours Taught</p>
                            </div>
                            <p className="text-2xl font-semibold" data-testid="scorecard-hours-taught">{scorecard.hours_taught}h</p>
                          </div>
                          <div className="p-4 rounded-xl border transition-shadow hover:shadow-[var(--shadow-md)]">
                            <div className="flex items-center gap-2 mb-2">
                              <Star className="h-4 w-4 text-amber-500" />
                              <p className="text-xs text-muted-foreground">Avg Rating</p>
                            </div>
                            <p className="text-2xl font-semibold" data-testid="scorecard-avg-rating">
                              {scorecard.avg_rating > 0 ? scorecard.avg_rating : '-'}
                              {scorecard.avg_rating > 0 && <span className="text-sm text-muted-foreground font-normal ml-1">/ 10</span>}
                            </p>
                            <p className="text-[10px] text-muted-foreground">{scorecard.total_reviews} review{scorecard.total_reviews !== 1 ? 's' : ''}</p>
                          </div>
                          <div className="p-4 rounded-xl border transition-shadow hover:shadow-[var(--shadow-md)]">
                            <div className="flex items-center gap-2 mb-2">
                              <AlertTriangle className={`h-4 w-4 ${scorecard.sessions_flagged > 0 ? 'text-amber-500' : 'text-slate-400'}`} />
                              <p className="text-xs text-muted-foreground">AI Flagged</p>
                            </div>
                            <p className="text-2xl font-semibold" data-testid="scorecard-flagged">{scorecard.sessions_flagged}</p>
                            {scorecard.sessions_flagged > 0 && (
                              <p className="text-[10px] text-amber-600">Needs review</p>
                            )}
                          </div>
                        </div>

                        {/* Rating Visualization */}
                        {scorecard.avg_rating > 0 && (
                          <div className="p-4 rounded-xl border bg-muted/30">
                            <h5 className="text-sm font-semibold mb-3 flex items-center gap-2"><Award className="h-4 w-4 text-amber-500" />Rating</h5>
                            <div className="flex items-center gap-3">
                              <div className="flex items-center gap-0.5">
                                {[1,2,3,4,5,6,7,8,9,10].map(i => (
                                  <Star key={i} className={`h-4 w-4 ${i <= Math.round(scorecard.avg_rating) ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />
                                ))}
                              </div>
                              <span className="text-lg font-semibold">{scorecard.avg_rating}/10</span>
                              <span className="text-sm text-muted-foreground">({scorecard.total_reviews} review{scorecard.total_reviews !== 1 ? 's' : ''})</span>
                            </div>
                          </div>
                        )}

                        {/* Quick Stats */}
                        <div className="p-4 rounded-xl border bg-muted/30">
                          <h5 className="text-sm font-semibold mb-3 flex items-center gap-2"><FileText className="h-4 w-4 text-primary" />Performance Summary</h5>
                          <div className="space-y-2">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">Sessions Completed</span>
                              <span className="font-medium">{scorecard.total_sessions}</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">Hours Taught</span>
                              <span className="font-medium">{scorecard.hours_taught} hrs</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">Average Rating</span>
                              <span className="font-medium">{scorecard.avg_rating > 0 ? `${scorecard.avg_rating} / 10` : 'No ratings yet'}</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">Sessions Flagged by AI</span>
                              <Badge variant="outline" className={scorecard.sessions_flagged > 0 ? 'bg-rose-50 text-rose-700 border-amber-200' : 'bg-emerald-50 text-emerald-800 border-emerald-200'}>
                                {scorecard.sessions_flagged > 0 ? `${scorecard.sessions_flagged} flagged` : 'Clean'}
                              </Badge>
                            </div>
                            {scorecard.bio && (
                              <div className="pt-2 border-t mt-2">
                                <span className="text-xs text-muted-foreground">Bio</span>
                                <p className="text-sm mt-1">{scorecard.bio}</p>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Individual Ratings (Superadmin sees learner names) */}
                        {instructorRatings && instructorRatings.ratings?.length > 0 && (
                          <div className="p-4 rounded-xl border bg-muted/30" data-testid="scorecard-individual-ratings">
                            <h5 className="text-sm font-semibold mb-3 flex items-center gap-2"><Star className="h-4 w-4 text-amber-500" />Individual Ratings</h5>
                            <div className="space-y-2 max-h-[250px] overflow-y-auto scrollbar-thin">
                              {instructorRatings.ratings.map((r, idx) => (
                                <div key={r.id || idx} className="flex items-start justify-between p-2.5 rounded-lg border bg-white hover:bg-muted/20 transition-colors">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <p className="text-sm font-medium truncate">{r.batch_name}</p>
                                      {r.scheduled_date && <span className="text-[10px] text-muted-foreground shrink-0">{formatDate(r.scheduled_date)}</span>}
                                    </div>
                                    {isSuperadmin && r.learner_name && (
                                      <p className="text-xs text-muted-foreground" data-testid={`rating-learner-name-${idx}`}>{r.learner_name} ({r.learner_email})</p>
                                    )}
                                    {r.comments && (
                                      <p className="text-xs text-muted-foreground mt-1 italic">"{r.comments}"</p>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-0.5 shrink-0 ml-2">
                                    {[1,2,3,4,5,6,7,8,9,10].map(i => (
                                      <Star key={i} className={`h-2.5 w-2.5 ${i <= r.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />
                                    ))}
                                    <span className="text-xs font-semibold ml-1">{r.rating}/10</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* MTD Payouts Tab (Instructor only) — with payout config */}
                <TabsContent value="payouts">
                  {/* Payout Rate Config */}
                  <div className="mb-5 p-4 rounded-xl border bg-muted/30">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold">Payout Rates</h4>
                      {!payoutEditing ? (
                        <Button variant="ghost" size="sm" onClick={() => setPayoutEditing(true)}><Pencil className="h-3 w-3 mr-1" />Edit Rates</Button>
                      ) : (
                        <div className="flex gap-1">
                          <Button size="sm" onClick={savePayoutConfig} disabled={payoutSaving}>
                            {payoutSaving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3 w-3 mr-1" />}Save
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setPayoutEditing(false)}>Cancel</Button>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                      {[
                        { key: 'rate_30min', label: '30 min' },
                        { key: 'rate_45min', label: '45 min' },
                        { key: 'rate_60min', label: '60 min' },
                        { key: 'rate_90min', label: '90 min' },
                        { key: 'no_show_rate', label: 'No-show' },
                      ].map(r => (
                        <div key={r.key} data-testid={`payout-rate-${r.key}`} className={`text-center p-2 rounded-lg bg-white border ${r.key === 'no_show_rate' ? 'border-amber-300 bg-amber-50/60' : ''}`}>
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">{r.label}</p>
                          {payoutEditing ? (
                            <Input data-testid={`payout-rate-input-${r.key}`} type="number" className="h-8 text-center text-sm font-semibold" value={payoutConfig[r.key] || ''} onChange={e => setPayoutConfig({...payoutConfig, [r.key]: parseFloat(e.target.value) || 0})} />
                          ) : (
                            <p className="text-lg font-semibold">{payoutConfig[r.key] ? `INR ${payoutConfig[r.key].toLocaleString()}` : <span className="text-muted-foreground text-sm">Not set</span>}</p>
                          )}
                        </div>
                      ))}
                    </div>
                    <p className="mt-2 text-[11px] text-muted-foreground">
                      No-show = instructor hosted the class and waited 15 min with no learner joining (1:1 and group alike). Leaving before 15 min pays INR 0.
                    </p>
                  </div>

                  {/* KPI Cards */}
                  <div className="grid grid-cols-4 gap-3 mb-4">
                    <div className="p-3 rounded-xl border bg-primary/5">
                      <p className="text-xs text-muted-foreground">All Sessions</p>
                      <p className="text-xl font-semibold mt-1">{payoutSessions.length}</p>
                    </div>
                    <div className="p-3 rounded-xl border">
                      <p className="text-xs text-muted-foreground">All Payouts</p>
                      <p className="text-xl font-semibold mt-1">{payoutSessions.length} <span className="text-xs text-muted-foreground font-normal">(INR {allPayoutAmount.toLocaleString()})</span></p>
                    </div>
                    <div className="p-3 rounded-xl border">
                      <p className="text-xs text-muted-foreground">In Review</p>
                      <p className="text-xl font-semibold mt-1">{payoutsInReview} <span className="text-xs text-muted-foreground font-normal">(INR {reviewPayoutAmount.toLocaleString()})</span></p>
                    </div>
                    <div className="p-3 rounded-xl border">
                      <p className="text-xs text-muted-foreground">Approved</p>
                      <p className="text-xl font-semibold mt-1">{payoutsApproved} <span className="text-xs text-muted-foreground font-normal">(INR {approvedPayoutAmount.toLocaleString()})</span></p>
                    </div>
                  </div>

                  {/* Session Payout List */}
                  <div className="space-y-2 max-h-[350px] overflow-y-auto scrollbar-thin">
                    <div className="flex items-center justify-between px-3 py-1.5 border-b text-xs text-muted-foreground font-medium">
                      <span>Session Details</span>
                      <div className="flex items-center gap-8">
                        <span>Payout Amount</span>
                        <span>Status</span>
                      </div>
                    </div>
                    {payoutSessions.map(s => {
                      const d = new Date(s.scheduled_date + 'T00:00:00');
                      const localStart2 = isIST() ? s.scheduled_time : convertISTToLocal(s.scheduled_time, s.scheduled_date);
                      const endTime = (() => {
                        const localTime = localStart2;
                        const [h, m] = (localTime || '0:0').split(':').map(Number);
                        const endMin = h * 60 + m + (s.duration_minutes || 60);
                        const eh = Math.floor(endMin / 60) % 24;
                        const em = endMin % 60;
                        return `${eh > 12 ? eh - 12 : eh || 12}:${String(em).padStart(2, '0')} ${eh >= 12 ? 'PM' : 'AM'}`;
                      })();
                      // Calculate payout from config — no-show sessions pay the flat
                      // no-show rate (INR 0 if the instructor left before 15 min).
                      const dur = s.duration_minutes || 60;
                      const waitMin = 15;
                      const isNoShow = !!s.no_show;
                      const rate = usingFinance
                        ? (s.payout_amount || 0)
                        : (isNoShow
                          ? ((s.no_show_waited_minutes ?? waitMin) < waitMin ? 0 : (payoutConfig.no_show_rate || 0))
                          : (dur <= 30 ? payoutConfig.rate_30min : dur <= 45 ? payoutConfig.rate_45min : dur <= 60 ? payoutConfig.rate_60min : payoutConfig.rate_90min));
                      const rateLabel = isNoShow
                        ? `No-show · waited ${Math.round(s.no_show_waited_minutes ?? 0)} min`
                        : `${dur}min rate`;
                      return (
                        <div key={s.id} className="flex items-center justify-between p-3 rounded-xl border hover:bg-muted/20">
                          <div className="flex items-center gap-3">
                            <div className="text-center min-w-[40px] bg-slate-100 rounded-lg px-2 py-1">
                              <p className="text-[9px] text-muted-foreground uppercase">{d.toLocaleDateString('en', { month: 'short' })}</p>
                              <p className="text-base font-semibold leading-tight">{d.getDate()}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium">{s.batch_name || s.title}</p>
                              {s.ai_summary?.suggested_title && <p className="text-xs text-muted-foreground">{s.ai_summary.suggested_title}</p>}
                              <p className="text-xs text-muted-foreground">{d.toLocaleDateString('en', { weekday: 'short' })} {formatTime(localStart2)} - {endTime}</p>
                              <Badge variant="outline" className={`${getStatusColor(s.status)} text-[10px] mt-0.5`}>{s.status}</Badge>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 text-right">
                            <div>
                              <p className="text-sm font-semibold">{rate ? `INR ${rate.toLocaleString()}` : (isNoShow ? 'INR 0' : '-')}</p>
                              <p className="text-[10px] text-muted-foreground">{rateLabel}</p>
                            </div>
                            {s.payout_approved ? (
                              <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200">Approved</Badge>
                            ) : (
                              <div className="flex items-center gap-1">
                                <Button size="sm" variant="outline" className="h-7 text-xs bg-emerald-50 border-emerald-200 text-emerald-800 hover:bg-emerald-100" onClick={async () => {
                                  try { await api.post(`/api/finance/approve-session/${s.id}`); toast.success('Approved'); const res = await api.get(`/api/users/${userId}/detail`); setUser(res.data); } catch { toast.error('Failed'); }
                                }}>Approve</Button>
                                <Badge variant="outline" className="bg-rose-50 text-rose-700 border-amber-200">Review</Badge>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {payoutSessions.length === 0 && (
                      <p className="text-center text-muted-foreground py-6">No completed sessions this month</p>
                    )}
                  </div>
                </TabsContent>

                {/* Communications Tab (Learner only) */}
                <TabsContent value="communications">
                  {user.communications?.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No communications recorded yet</p>
                  ) : (
                    <div className="space-y-2">
                      {user.communications?.map((c, i) => (
                        <div key={i} className="flex items-center justify-between p-3 rounded-xl border">
                          <div className="flex items-center gap-3">
                            <div className="h-8 w-8 rounded-lg bg-blue-50 flex items-center justify-center">
                              <Mail className="h-4 w-4 text-blue-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium">{c.action?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                              <p className="text-xs text-muted-foreground">{c.batch_name || ''}</p>
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground">{formatDate(c.timestamp)}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* V16.3 — Feedback Tab (learner): every rating + review the
                    learner submitted, with instructor + class + timeslot */}
                <TabsContent value="feedback">
                  {!feedbackList ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : (feedbackList.feedback || []).length === 0 ? (
                    <p className="text-center text-muted-foreground py-8" data-testid="feedback-empty">
                      No feedback submitted yet
                    </p>
                  ) : (
                    <div className="space-y-3" data-testid="feedback-list">
                      <div className="flex items-center gap-2 p-3 rounded-xl border bg-muted/40" data-testid="feedback-summary">
                        <Star className="h-4 w-4 text-amber-500 fill-amber-400" />
                        <p className="text-sm font-medium">
                          Average rating: {feedbackList.avg_rating ?? '—'}<span className="text-muted-foreground font-normal">/10</span>
                        </p>
                        <span className="text-xs text-muted-foreground">
                          · {feedbackList.total} review{feedbackList.total === 1 ? '' : 's'}
                        </span>
                      </div>
                      {feedbackList.feedback.map((f) => {
                        const ratingCls = f.rating >= 8
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          : f.rating >= 5
                            ? 'bg-amber-50 text-amber-700 border-amber-200'
                            : 'bg-rose-50 text-rose-700 border-rose-200';
                        let slot = '';
                        if (f.scheduled_time) {
                          const [h, m] = f.scheduled_time.split(':').map(Number);
                          const start = new Date(2000, 0, 1, h, m);
                          const end = new Date(2000, 0, 1, h, m + (f.duration_minutes || 60));
                          const fmt = (d) => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                          slot = `${fmt(start)} – ${fmt(end)}`;
                        }
                        return (
                          <div key={f.id} className="p-4 rounded-xl border hover:bg-muted/20 transition-colors" data-testid={`feedback-row-${f.id}`}>
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex items-start gap-3 min-w-0">
                                <div className="h-9 w-9 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
                                  <Star className="h-4 w-4 text-amber-600" />
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm font-medium truncate" data-testid={`feedback-instructor-${f.id}`}>
                                    {f.instructor_name || 'Instructor not recorded'}
                                  </p>
                                  <p className="text-xs text-muted-foreground truncate">
                                    {f.batch_name || f.session_title}
                                    {f.course_type ? ` · ${f.course_type}` : ''}
                                    {f.batch_type === 'one_on_one' ? ' · 1:1' : f.batch_type === 'group' ? ' · Group' : ''}
                                  </p>
                                  <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                                    <Calendar className="h-3 w-3" />
                                    {f.scheduled_date ? formatDate(f.scheduled_date) : '—'}
                                    {slot && (
                                      <>
                                        <Clock className="h-3 w-3 ml-1" />
                                        {slot}
                                      </>
                                    )}
                                  </p>
                                </div>
                              </div>
                              <Badge variant="outline" className={`${ratingCls} shrink-0 font-semibold`} data-testid={`feedback-rating-${f.id}`}>
                                <Star className="h-3 w-3 mr-1 fill-current" />
                                {f.rating}/10
                              </Badge>
                            </div>
                            {f.comments && (
                              <p className="text-sm mt-2 pl-12 text-foreground/90 italic" data-testid={`feedback-comment-${f.id}`}>
                                “{f.comments}”
                              </p>
                            )}
                            {f.created_at && (
                              <p className="text-[11px] text-muted-foreground mt-1.5 pl-12">
                                Submitted {formatDate(f.created_at)}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </TabsContent>

                {/* V54 — SoulAI (pre-recorded coach) usage: watch history, minutes
                    watched per class, upcoming reminder classes and her rating per class */}
                <TabsContent value="soulai">
                  {tab === 'soulai' && <SoulAIUsageTab userId={userId} />}
                </TabsContent>

                {/* V68 — Diet Communication: nutritionist chats, consult calls and their
                    recordings in one timeline. Mounted only when the tab is clicked, and every
                    query inside is paginated (see routers/admin_diet_comms.py) so this stays
                    cheap at millions of conversations. */}
                <TabsContent value="diet_comms">
                  {tab === 'diet_comms' && <DietCommunicationTab userId={userId} />}
                </TabsContent>

                {/* Working Hours Tab (Instructor only) */}
                <TabsContent value="working_hours">
                  {(() => {
                    const DAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                    const addAvailSlot = (day) => setAvailSlots(prev => [...prev, { instructor_id: userId, day_of_week: day, start_time: '09:00', end_time: '17:00' }]);
                    const updateAvailSlot = (idx, field, value) => setAvailSlots(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
                    const removeAvailSlot = (idx) => setAvailSlots(prev => prev.filter((_, i) => i !== idx));
                    const saveAvail = async () => {
                      setAvailSaving(true);
                      try {
                        await api.post('/api/availability', { instructor_id: userId, slots: availSlots.map(s => ({ ...s, instructor_id: userId })) });
                        toast.success('Working hours saved');
                      } catch { toast.error('Failed to save'); }
                      setAvailSaving(false);
                    };
                    return (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs text-muted-foreground">Timezone: Asia/Kolkata (GMT +05:30)</p>
                          <Button size="sm" onClick={saveAvail} disabled={availSaving}>
                            {availSaving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3 w-3 mr-1" />}Save
                          </Button>
                        </div>
                        {[0,1,2,3,4,5,6].map(day => {
                          const daySlots = availSlots.filter(s => s.day_of_week === day);
                          const hasSlots = daySlots.length > 0;
                          return (
                            <div key={day} className="border-b pb-3">
                              <div className="flex items-center justify-between mb-2">
                                <label className="flex items-center gap-2 cursor-pointer">
                                  <input type="checkbox" checked={hasSlots} onChange={() => { if (hasSlots) { setAvailSlots(prev => prev.filter(s => s.day_of_week !== day)); } else { addAvailSlot(day); }}} className="w-4 h-4 rounded" />
                                  <span className="text-sm font-medium">{DAY_LABELS[day]}</span>
                                </label>
                                {!hasSlots && <span className="text-xs text-muted-foreground">Unavailable</span>}
                              </div>
                              {daySlots.map((slot, i) => {
                                const globalIdx = availSlots.indexOf(slot);
                                return (
                                  <div key={i} className="flex items-center gap-2 ml-6 mb-1.5">
                                    <Input type="time" value={slot.start_time} onChange={e => updateAvailSlot(globalIdx, 'start_time', e.target.value)} className="w-28 h-8 text-sm" />
                                    <span className="text-muted-foreground">-</span>
                                    <Input type="time" value={slot.end_time} onChange={e => updateAvailSlot(globalIdx, 'end_time', e.target.value)} className="w-28 h-8 text-sm" />
                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => removeAvailSlot(globalIdx)}><Trash2 className="h-3 w-3" /></Button>
                                    {i === daySlots.length - 1 && (
                                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => addAvailSlot(day)}><Plus className="h-3 w-3" /></Button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </TabsContent>

              </Tabs>
            </div>
          </div>
        ) : (
          <div className="p-6 text-center"><p className="text-muted-foreground">User not found</p></div>
        )}
      </DialogContent>
    </Dialog>
  );
}




// ── V63.5 — Delivery date (EDD) override, reachable from the profile header ──
// It used to live only inside the Batches tab under "Pregnancy Details", which is why
// the team could not find it. Changing the date re-derives her stage and rebuilds this
// week's coach plan server-side, and every change is audited with the reason.
function EddOverrideDialog({ open, user, onClose, onSaved }) {
  const pp = user?.pregnancy_profile || {};
  const [edd, setEdd] = useState((pp.edd || '').slice(0, 10));
  const [reason, setReason] = useState('');
  const [lock, setLock] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setEdd((user?.pregnancy_profile?.edd || '').slice(0, 10)); setReason(''); setLock(true); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user?.id]);

  const save = async () => {
    if (!edd) return toast.error('Pick the delivery date');
    if (reason.trim().length < 3) return toast.error('Add a short reason — it goes in the audit trail');
    setBusy(true);
    try {
      const { data } = await api.post(`/api/admin/users/${user.id}/edd-override`, {
        edd, reason: reason.trim(), permanent_lock: lock,
      });
      toast.success(data?.stage_healed_to
        ? `Delivery date updated · stage now ${data.stage_healed_to}${data.coach_plan_rebuilt ? ' · plan rebuilt' : ''}`
        : `Delivery date updated${data?.coach_plan_rebuilt ? ' · plan rebuilt' : ''}`);
      onClose();
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.response?.data?.detailObject?.code || 'Could not update the delivery date');
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="edd-override-dialog">
        <DialogTitle>Change delivery date</DialogTitle>
        <div className="space-y-3 text-sm">
          <p className="text-xs text-muted-foreground">
            {pp.edd ? <>Current due date <strong>{new Date(pp.edd).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</strong></> : 'No due date on file yet.'}
            {pp.edd_locked_at ? ' · locked for the learner' : ''}
          </p>
          <div>
            <label className="text-xs font-medium">New due date (EDD)</label>
            <Input type="date" value={edd} onChange={(e) => setEdd(e.target.value)}
              data-testid="edd-override-date" className="mt-1" />
          </div>
          <div>
            <label className="text-xs font-medium">Reason</label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1"
              placeholder="e.g. scan report shows 4 Mar, corrected on call"
              data-testid="edd-override-reason" />
          </div>
          <label className="flex items-start gap-2 text-xs">
            <input type="checkbox" checked={lock} onChange={(e) => setLock(e.target.checked)}
              className="mt-0.5" data-testid="edd-override-lock" />
            <span>Lock it — the learner cannot change this date herself afterwards</span>
          </label>
          <p className="text-[11px] text-muted-foreground">
            Her stage (prenatal / postnatal) and this week's SoulAI Coach plan are recalculated automatically.
          </p>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
            <Button size="sm" onClick={save} disabled={busy} data-testid="edd-override-save">
              {busy ? 'Saving…' : 'Save delivery date'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── V13.14.5 — Admin EDD override control ──
function AdminEddOverrideControl({ userId, currentEdd, onChanged }) {
  const [open, setOpen] = useState(false);
  const [newEdd, setNewEdd] = useState((currentEdd || '').slice(0, 10));
  const [reason, setReason] = useState('');
  const [permanentLock, setPermanentLock] = useState(true);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!newEdd) return toast.error('Pick a new EDD');
    if (!reason || reason.trim().length < 3) return toast.error('Enter a reason (min 3 chars)');
    setBusy(true);
    try {
      await api.post(`/api/admin/users/${userId}/edd-override`, {
        edd: newEdd, reason, permanent_lock: permanentLock,
      });
      toast.success('EDD overridden');
      setOpen(false);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
    setBusy(false);
  };

  if (!open) {
    return (
      <Button variant="outline" size="sm" className="mt-2 text-xs" onClick={() => setOpen(true)}
              data-testid="admin-edd-override-btn">
        Override EDD
      </Button>
    );
  }
  return (
    <div className="mt-2 p-3 border border-amber-200 bg-amber-50 rounded-lg space-y-2"
         data-testid="admin-edd-override-form">
      <p className="text-[11px] font-medium">Admin EDD override</p>
      <Input type="date" value={newEdd} onChange={(e) => setNewEdd(e.target.value)}
             className="h-8 text-xs" data-testid="admin-edd-override-date" />
      <Input placeholder="Reason (audit trail)" value={reason}
             onChange={(e) => setReason(e.target.value)} className="h-8 text-xs"
             data-testid="admin-edd-override-reason" />
      <label className="flex items-center gap-2 text-[11px]">
        <input type="checkbox" checked={permanentLock}
               onChange={(e) => setPermanentLock(e.target.checked)}
               data-testid="admin-edd-override-lock" />
        Permanently lock (learner cannot edit after this)
      </label>
      <div className="flex gap-1">
        <Button size="sm" onClick={submit} disabled={busy}
                data-testid="admin-edd-override-save" className="text-xs">
          {busy ? 'Saving…' : 'Save'}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)} className="text-xs">
          Cancel
        </Button>
      </div>
    </div>
  );
}


// ── V14.5 Part 17l — Revert a mis-flipped learner back to prenatal ──
function AdminRevertPostnatalControl({ userId, courseType, flippedVia, onChanged }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [correctedEdd, setCorrectedEdd] = useState('');
  const [busy, setBusy] = useState(false);

  // Visible only when the user is currently postnatal AND the flip was
  // performed automatically by the cron (the case that usually needs
  // reverting because of a bad EDD). For manually-flipped users, the
  // admin should use the standard pregnancy-profile editor instead.
  if (courseType !== 'postnatal') return null;
  const isAutoFlipped = flippedVia === 'auto_postnatal';

  const submit = async () => {
    if (!reason || reason.trim().length < 3) return toast.error('Enter a reason (min 3 chars)');
    setBusy(true);
    try {
      const payload = { reason };
      if (correctedEdd) payload.corrected_edd = correctedEdd;
      await api.post(`/api/admin/users/${userId}/revert-postnatal`, payload);
      toast.success('Reverted to prenatal');
      setOpen(false);
      setReason('');
      setCorrectedEdd('');
      onChanged?.();
    } catch (e) {
      // After the 17k interceptor, `detail` is always a friendly string.
      toast.error(e?.response?.data?.detail || 'Revert failed');
    }
    setBusy(false);
  };

  if (!open) {
    return (
      <Button
        variant="outline" size="sm"
        className="mt-2 ml-2 text-xs border-rose-300 text-rose-700 hover:bg-rose-50"
        onClick={() => setOpen(true)}
        data-testid="admin-revert-postnatal-btn"
      >
        Revert to Prenatal{isAutoFlipped ? ' (auto-flip)' : ''}
      </Button>
    );
  }
  return (
    <div className="mt-2 p-3 border border-rose-200 bg-rose-50 rounded-lg space-y-2"
         data-testid="admin-revert-postnatal-form">
      <p className="text-[11px] font-medium">Revert this learner to prenatal</p>
      {isAutoFlipped && (
        <p className="text-[10px] text-rose-700">
          ⚠ Detected: this user was auto-flipped to postnatal by the overdue-EDD cron.
          The most common cause is an EDD typo (e.g. date-of-birth typed in as EDD).
          You can optionally fix the EDD in the same step below.
        </p>
      )}
      <Input
        type="date" value={correctedEdd}
        onChange={(e) => setCorrectedEdd(e.target.value)}
        placeholder="Optional: correct EDD"
        className="h-8 text-xs"
        data-testid="admin-revert-postnatal-edd"
      />
      <Input
        placeholder="Reason (audit trail, min 3 chars)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        className="h-8 text-xs"
        data-testid="admin-revert-postnatal-reason"
      />
      <div className="flex gap-1">
        <Button size="sm" onClick={submit} disabled={busy}
                data-testid="admin-revert-postnatal-save" className="text-xs bg-rose-600 hover:bg-rose-700">
          {busy ? 'Reverting…' : 'Confirm revert'}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => { setOpen(false); setReason(''); setCorrectedEdd(''); }} className="text-xs">
          Cancel
        </Button>
      </div>
    </div>
  );
}



// ── Prenatal AI Coach — per-user access toggle (temporary until GA) ──
function PrenatalCoachAccessControl({ userId }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    if (!userId) return;
    api.get(`/api/admin/prenatal-coach/users/${userId}/access`)
      .then((r) => setState(r.data)).catch(() => {});
  }, [userId]);

  const toggle = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/api/admin/prenatal-coach/users/${userId}/access`, { enabled: !state.enabled });
      setState((s) => ({ ...s, enabled: res.data.enabled }));
      toast.success(res.data.enabled ? 'SoulAI Coach enabled for this user' : 'SoulAI Coach disabled');
    } catch { toast.error('Failed to update access'); }
    setBusy(false);
  };

  const doReset = async () => {
    setResetting(true);
    try {
      await api.post(`/api/admin/prenatal-coach/users/${userId}/reset`);
      setState((s) => ({ ...s, onboarded: false }));
      toast.success('SoulAI Coach reset — the user will start a fresh onboarding');
      setConfirmReset(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reset'); }
    setResetting(false);
  };

  if (!state) return null;
  return (
    <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="prenatal-coach-access-control">
      <Badge variant="outline" className={state.enabled ? 'bg-emerald-50 text-emerald-800 border-emerald-200 text-[10px]' : 'text-[10px]'}>
        SoulAI Coach admin toggle: {state.enabled ? 'On (full access)' : 'Off'}
      </Badge>
      {state.onboarded && <Badge variant="outline" className="text-[10px]">Onboarded ✓</Badge>}
      <Button variant="outline" size="sm" className="text-xs h-7" onClick={toggle} disabled={busy}
        data-testid="prenatal-coach-access-toggle">
        {busy ? 'Saving…' : state.enabled ? 'Disable Coach access' : 'Enable Coach access'}
      </Button>
      {state.onboarded && (
        <Button variant="outline" size="sm" className="text-xs h-7 text-rose-700 border-rose-200 hover:bg-rose-50"
          onClick={() => setConfirmReset(true)} disabled={resetting}
          data-testid="prenatal-coach-reset-btn">
          Reset SoulAI
        </Button>
      )}

      <Dialog open={confirmReset} onOpenChange={(v) => !v && setConfirmReset(false)}>
        <DialogContent className="max-w-sm" data-testid="prenatal-coach-reset-modal">
          <DialogHeader>
            <DialogTitle>Reset SoulAI Coach?</DialogTitle>
            <DialogDescription>
              This clears the user's onboarding answers, weekly plans and coach appointments.
              They'll start a fresh onboarding next time they open SoulAI Coach. Coach access
              stays enabled. This can't be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmReset(false)} disabled={resetting}
              data-testid="prenatal-coach-reset-cancel">Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={doReset} disabled={resetting}
              data-testid="prenatal-coach-reset-confirm">
              {resetting ? 'Resetting…' : 'Reset'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ── V21 §3.4 — Support releases a phone number so another learner can verify it ──
function ReleasePhoneDialog({ open, user, onClose, onReleased }) {
  const [busy, setBusy] = useState(false);
  const [needsForce, setNeedsForce] = useState(false);
  const release = async (force = false) => {
    setBusy(true);
    try {
      await api.post(`/api/users/${user.id}/release-phone`, force ? { force: true } : {});
      const res = await api.get(`/api/users/${user.id}/detail`);
      toast.success(`Released ${user.phone} — the number can now be verified on another account`);
      onReleased(res.data); setNeedsForce(false); onClose();
    } catch (err) {
      const d = err?.response?.data?.detailObject;
      if (d?.code === 'PHONE_IS_ONLY_LOGIN') { setNeedsForce(true); return; }
      toast.error(d?.message || err?.response?.data?.detail || 'Could not release the number');
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setNeedsForce(false); onClose(); } }}>
      <DialogContent className="max-w-sm" data-testid="release-phone-dialog">
        <DialogHeader>
          <DialogTitle>Release {user.phone}?</DialogTitle>
          <DialogDescription>
            Unlinks this number from {user.name || 'the learner'}'s account and clears its verified status, so another
            learner can verify it. They will be asked to verify a number again at next login.
          </DialogDescription>
        </DialogHeader>
        {needsForce && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="release-phone-force-warning">
            This number is the account's <b>only</b> way to sign in (no password, no Google). Releasing it will lock the learner out.
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => { setNeedsForce(false); onClose(); }} disabled={busy} data-testid="release-phone-cancel">Cancel</Button>
          {needsForce ? (
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={() => release(true)} disabled={busy} data-testid="release-phone-force-confirm">
              {busy ? 'Releasing…' : 'Release anyway'}
            </Button>
          ) : (
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={() => release(false)} disabled={busy} data-testid="release-phone-confirm">
              {busy ? 'Releasing…' : 'Release number'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ── V13.14.5 — Admin postnatal-flip unlock (early delivery) ──
function AdminPostnatalUnlockControl({ userId, unlocked, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState('');
  const [showReason, setShowReason] = useState(false);

  const toggle = async () => {
    if (!showReason) { setShowReason(true); return; }
    if (!reason || reason.trim().length < 3) return toast.error('Reason (min 3 chars)');
    setBusy(true);
    try {
      await api.post(`/api/admin/users/${userId}/postnatal-unlock`, {
        unlock: !unlocked, reason,
      });
      toast.success(unlocked ? 'Re-locked' : 'Postnatal switch enabled');
      setShowReason(false);
      setReason('');
      onChanged?.();
    } catch { toast.error('Failed'); }
    setBusy(false);
  };

  if (!showReason) {
    return (
      <Button variant="outline" size="sm" className="mt-1 text-xs ml-2"
              onClick={() => setShowReason(true)}
              data-testid="admin-postnatal-unlock-btn">
        {unlocked ? 'Re-lock postnatal switch' : 'Allow postnatal switch (early delivery)'}
      </Button>
    );
  }
  return (
    <div className="mt-2 p-3 border border-rose-200 bg-rose-50 rounded-lg space-y-2"
         data-testid="admin-postnatal-unlock-form">
      <Input placeholder="Reason (e.g. delivered 4 weeks early)" value={reason}
             onChange={(e) => setReason(e.target.value)} className="h-8 text-xs"
             data-testid="admin-postnatal-unlock-reason" />
      <div className="flex gap-1">
        <Button size="sm" onClick={toggle} disabled={busy} className="text-xs"
                data-testid="admin-postnatal-unlock-save">
          {busy ? 'Saving…' : 'Confirm'}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setShowReason(false)}
                className="text-xs">Cancel</Button>
      </div>
    </div>
  );
}


// ─── V13.14.6 — Fees panel (admin view of learner payment history) ───
function FeesPanel({ userId, userName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [recordOpen, setRecordOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const fetchPayments = () => {
    if (!userId) return;
    setLoading(true);
    api.get(`/api/payment/admin/users/${userId}/payments`, { params: { limit: 100 } })
      .then(res => setData(res.data))
      .catch((err) => {
        toast.error(err.response?.data?.detail || 'Failed to load payments');
        setData({ transactions: [], total_spent: 0, count: 0 });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchPayments(); /* eslint-disable-next-line */ }, [userId]);

  const syncFromOps = async () => {
    setSyncing(true);
    try {
      const res = await api.post(`/api/payment/admin/users/${userId}/payments/sync-from-ops`);
      const { fetched = 0, inserted = 0, enriched = 0, duplicates = 0, ops_status = 'ok', fallback_inserted = 0 } = res.data || {};
      if (inserted > 0) {
        toast.success(fallback_inserted > 0 && fetched === 0
          ? `Recovered ${fallback_inserted} payment${fallback_inserted === 1 ? '' : 's'} from the OPS enrollment record`
          : `Synced ${inserted} new payment${inserted === 1 ? '' : 's'} from OPS${enriched ? ` · ${enriched} updated with invoice links` : ''}`);
      } else if (enriched > 0) {
        toast.success(`${enriched} payment${enriched === 1 ? '' : 's'} updated with OPS invoice / receipt links`);
      } else if (fetched > 0) {
        toast.info(`Already in sync: ${duplicates} payment${duplicates === 1 ? '' : 's'} on file at OPS`);
      } else if (ops_status === 'endpoint_missing') {
        toast.warning('OPS has not enabled its payment-history API yet (GET /lms/users/payments → 404). Payments arrive via the enrollment webhook; use Record Payment for anything older.', { duration: 9000 });
      } else if (ops_status === 'error') {
        toast.error('Could not reach OPS payment history — try again in a minute');
      } else {
        toast.info('No payments found at OPS for this learner');
      }
      fetchPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync failed');
    }
    setSyncing(false);
  };

  const downloadReceipt = async (tx) => {
    // Prefer Razorpay receipt if OPS provided one
    if (tx.receipt_url) {
      window.open(tx.receipt_url, '_blank', 'noopener');
      return;
    }
    setDownloadingId(tx.id);
    try {
      const ref = tx.payment_id || tx.id;
      const res = await api.get(
        `/api/payment/admin/users/${userId}/payments/${ref}/receipt`,
        { responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `SSA-Receipt-${ref.slice(-10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      const msg = err.response?.data?.detail
        || 'Receipt unavailable';
      toast.error(typeof msg === 'string' ? msg : 'Receipt unavailable');
    } finally {
      setDownloadingId(null);
    }
  };

  const statusPillClass = (status) => {
    switch (status) {
      case 'completed': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'pending':   return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'failed':    return 'bg-rose-50 text-rose-700 border-rose-200';
      case 'refunded':  return 'bg-slate-100 text-slate-700 border-slate-200';
      default:          return 'bg-slate-50 text-slate-700 border-slate-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="fees-panel-loading">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const txs = data?.transactions || [];

  return (
    <div data-testid="fees-panel">
      {/* Header with action buttons */}
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <p className="text-sm text-muted-foreground">
          {txs.length === 0 ? 'Record any payment captured outside the OPS webhook.' : `${txs.length} transaction${txs.length === 1 ? '' : 's'} on file`}
        </p>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={syncFromOps}
            disabled={syncing}
            data-testid="fees-sync-from-ops-btn"
            title="Pull payments from OPS for this learner"
          >
            {syncing ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
            Sync from OPS
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setRecordOpen(true)}
            data-testid="fees-record-payment-btn"
          >
            <Plus className="h-3.5 w-3.5 mr-1.5" />Record Payment
          </Button>
        </div>
      </div>

      {/* Summary card */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
        <div className="p-3 rounded-xl border bg-primary/5">
          <p className="text-xs text-muted-foreground">Total Paid</p>
          <p className="text-lg font-semibold" data-testid="fees-total-spent">
            {(data?.currency || 'INR') === 'INR' ? '₹' : `${data?.currency} `}{(data?.total_spent || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
          </p>
        </div>
        <div className="p-3 rounded-xl border">
          <p className="text-xs text-muted-foreground">Transactions</p>
          <p className="text-lg font-semibold" data-testid="fees-tx-count">{txs.length}</p>
        </div>
        <div className="p-3 rounded-xl border">
          <p className="text-xs text-muted-foreground">Currency</p>
          <p className="text-lg font-semibold">{data?.currency || 'INR'}</p>
        </div>
      </div>

      {/* Transaction table */}
      {txs.length === 0 ? (
        <div className="text-center py-10 border rounded-xl bg-muted/20">
          <CreditCard className="h-8 w-8 mx-auto text-muted-foreground/40 mb-2" />
          <p className="text-sm text-muted-foreground">No payments on file for {userName || 'this learner'}.</p>
          <p className="text-xs text-muted-foreground/70 mt-1 max-w-md mx-auto">
            New OPS-Razorpay transactions sync automatically. For wire transfers, UPI,
            cash, or pre-existing enrolments, click <strong>Record Payment</strong> above.
          </p>
        </div>
      ) : (
        <div className="border rounded-xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Item</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right w-20">Receipt</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {txs.map(tx => {
                const date = tx.completed_at || tx.created_at;
                const dateStr = date ? formatDate(date) : '—';
                const amount = `${tx.currency === 'INR' ? '₹' : tx.currency + ' '}${(tx.amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
                const canDownload = tx.status === 'completed';
                return (
                  <TableRow key={tx.id} data-testid={`fees-row-${tx.id}`}>
                    <TableCell className="text-sm whitespace-nowrap">{dateStr}</TableCell>
                    <TableCell>
                      <p className="text-sm font-medium">{tx.purpose_label}</p>
                      {tx.payment_id && (
                        <p className="text-[10px] text-muted-foreground font-mono truncate max-w-[180px]">
                          {tx.payment_id}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-sm font-semibold">{amount}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`${statusPillClass(tx.status)} text-[10px] capitalize`}>
                        {tx.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {tx.invoice_url && (
                        <Button size="sm" variant="ghost" className="h-7 text-xs text-primary hover:text-primary"
                          onClick={() => window.open(tx.invoice_url, '_blank', 'noopener')}
                          data-testid={`fees-invoice-${tx.id}`} title={`OPS invoice ${tx.invoice_number || ''}`}>
                          <FileText className="h-3 w-3 mr-1" />{tx.invoice_number || 'Invoice'}
                        </Button>
                      )}
                      {canDownload ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs text-primary hover:text-primary"
                          onClick={() => downloadReceipt(tx)}
                          disabled={downloadingId === tx.id}
                          data-testid={`fees-download-${tx.id}`}
                          title={tx.receipt_url ? 'Open Razorpay receipt' : 'Download PDF receipt'}
                        >
                          {downloadingId === tx.id
                            ? <Loader2 className="h-3 w-3 animate-spin" />
                            : (tx.receipt_url ? <ExternalLink className="h-3 w-3" /> : <Download className="h-3 w-3" />)}
                        </Button>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <p className="text-[11px] text-muted-foreground mt-3 px-1">
        Payment data is synced from the OPS panel via webhook. Receipts default to the
        Razorpay link when available; otherwise a Soul Sanskar branded PDF is generated.
      </p>

      {/* Record Payment dialog */}
      <RecordPaymentDialog
        open={recordOpen}
        onClose={() => setRecordOpen(false)}
        userId={userId}
        userName={userName}
        onSuccess={() => { setRecordOpen(false); fetchPayments(); }}
      />
    </div>
  );
}


// ─── V13.14.6 — Manually record a payment for a learner ───
// Used when payment was captured outside the OPS webhook flow (wire transfer,
// UPI, cash, OPS-side admin entry without webhook fire). Posts to
// /api/payment/admin/users/{id}/payments — see backend payments.py.
function RecordPaymentDialog({ open, onClose, userId, userName, onSuccess }) {
  const todayISO = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    amount: '',
    method: 'razorpay',
    purpose: 'enrollment',
    payment_id: '',
    paid_on: todayISO,
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  // Reset form when dialog re-opens (so we don't carry stale state across users)
  useEffect(() => {
    if (open) {
      setForm({
        amount: '', method: 'razorpay', purpose: 'enrollment',
        payment_id: '', paid_on: todayISO, notes: '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userId]);

  const submit = async () => {
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/api/payment/admin/users/${userId}/payments`, {
        amount: amt,
        currency: 'INR',
        method: form.method,
        purpose: form.purpose,
        payment_id: form.payment_id.trim() || null,
        paid_on: form.paid_on || null,
        notes: form.notes.trim() || null,
      });
      toast.success('Payment recorded');
      onSuccess?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const code = err.response?.data?.detailObject?.code;
      const msg = (code === 'PAYMENT_EXISTS' ? 'A payment with that reference is already on file.' : null)
        || (typeof detail === 'string' ? detail : 'Failed to record payment');
      toast.error(msg);
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !saving && onClose()}>
      <DialogContent data-testid="record-payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Record Payment</DialogTitle>
          <DialogDescription>
            For {userName || 'this learner'} · captured outside the OPS webhook
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium mb-1 block">Amount (INR) *</label>
              <Input
                type="number" min="1" step="0.01" inputMode="decimal"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="35000"
                data-testid="record-payment-amount-input"
                autoFocus
              />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block">Paid on *</label>
              <Input
                type="date" value={form.paid_on}
                onChange={(e) => setForm({ ...form, paid_on: e.target.value })}
                max={todayISO}
                data-testid="record-payment-date-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium mb-1 block">Method</label>
              <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                <SelectTrigger data-testid="record-payment-method-select"><SelectValue /></SelectTrigger>
                <SelectContent position="popper" sideOffset={4} className="z-[200]">
                  <SelectItem value="razorpay">Razorpay (OPS)</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer / NEFT</SelectItem>
                  <SelectItem value="card">Card (offline)</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block">Purpose</label>
              <Select value={form.purpose} onValueChange={(v) => setForm({ ...form, purpose: v })}>
                <SelectTrigger data-testid="record-payment-purpose-select"><SelectValue /></SelectTrigger>
                <SelectContent position="popper" sideOffset={4} className="z-[200]">
                  <SelectItem value="enrollment">Course Enrollment</SelectItem>
                  <SelectItem value="elite_monthly">Elite Monthly</SelectItem>
                  <SelectItem value="elite_quarterly">Elite Quarterly</SelectItem>
                  <SelectItem value="garbhsanskar_full">Garbhsanskar Full</SelectItem>
                  <SelectItem value="garbhsanskar_addon">Garbhsanskar Trial</SelectItem>
                  <SelectItem value="trial_class">Trial Class</SelectItem>
                  <SelectItem value="gynae_consult">Gynae Consultation</SelectItem>
                  <SelectItem value="nutritionist_consult">Nutritionist Consultation</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium mb-1 block">
              Razorpay / Transaction Ref <span className="text-muted-foreground font-normal">(optional, recommended)</span>
            </label>
            <Input
              value={form.payment_id}
              onChange={(e) => setForm({ ...form, payment_id: e.target.value })}
              placeholder="pay_xxx, UPI ref, NEFT UTR…"
              className="font-mono text-xs"
              data-testid="record-payment-ref-input"
            />
            <p className="text-[10px] text-muted-foreground mt-1">
              Lets us prevent duplicates and link to OPS records. Leave blank if not available.
            </p>
          </div>

          <div>
            <label className="text-xs font-medium mb-1 block">Notes <span className="text-muted-foreground font-normal">(optional)</span></label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="e.g. Annual Elite — bank transfer received Apr 30"
              className="min-h-[60px]"
              data-testid="record-payment-notes-input"
              maxLength={500}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !form.amount} data-testid="record-payment-submit-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Record
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

